<template>
  <div class="printer-layout">
    <!-- 头部区域 -->
    <header class="header">
      <div class="header-title">
        <img src="/img/dyj.png" alt="打印机" class="header-icon">
        <h1>润枫池 打印系统</h1>
      </div>
    </header>

    <!-- 主内容区域 -->
    <main class="main-content">
      <!-- 打印机控制面板 -->
      <div class="printer-panel">
        <div class="panel-header">
          <h2>打印机设置</h2>
        </div>

        <!-- 打印机选择器 -->
        <div class="printer-selector">
          <label class="selector-label">选择打印机：</label>
          <select
            v-model="selectedPrinter"
            class="printer-select"
            @change="handlePrinterChange"
            :disabled="isLoadingPrinters"
          >
            <option value="" v-if="isLoadingPrinters">加载中...</option>
            <option value="" v-else>请选择打印机</option>
            <option
              v-for="printer in availablePrinters"
              :key="printer.id"
              :value="printer.id"
            >
              {{ printer.name }}{{ printer.isDefault ? ' (默认)' : '' }}
            </option>
          </select>
          <button
            class="refresh-btn"
            @click="refreshPrinters"
            :disabled="isLoadingPrinters"
            title="刷新打印机列表"
          >
            <img
              src="/img/refresh.png"
              alt="刷新"
              class="refresh-icon"
              :class="{ 'spinning': isLoadingPrinters }"
            >
            刷新
          </button>
        </div>

        <!-- 打印功能区域 -->
        <div class="print-functions">
          <h3>打印功能</h3>

          <!-- 订单打印循环 -->
          <div class="order-print-section">
            <p class="section-desc">自动检查并打印待处理订单</p>
            <div class="order-controls">
              <button
                class="order-check-btn"
                @click="toggleAutoPrint"
                :class="{ active: autoPrintEnabled }"
              >
                {{ autoPrintEnabled ? '停止自动打印' : '开启自动打印' }}
              </button>
              <button
                class="order-check-btn"
                @click="checkPendingOrders"
                :disabled="isCheckingOrders"
              >
                <span v-if="isCheckingOrders">检查中...</span>
                <span v-else>手动检查订单</span>
              </button>
            </div>

            <!-- 状态显示 -->
            <div class="print-status" v-if="lastCheckTime">
              <p>最后检查时间: {{ lastCheckTime }}</p>
              <p v-if="pendingOrdersCount > 0" class="pending-count">
                待处理订单: <span>{{ pendingOrdersCount }}</span>
              </p>
            </div>
          </div>

          <!-- 测试打印 -->
          <div class="test-print-section">
            <h4>打印机测试</h4>
            <button
              class="test-print-btn"
              @click="testPrinter"
              :disabled="!selectedPrinter || isLoadingPrinters"
            >
              <img src="/img/dyj.png" alt="打印测试" class="btn-icon">
              测试打印
            </button>
          </div>
        </div>
      </div>

      <!-- 状态信息区域 -->
      <div class="status-panel">
        <div class="panel-header">
          <h2>状态信息</h2>
        </div>

        <div class="status-info">
          <div class="status-item">
            <label>当前打印机：</label>
            <span class="status-value">
              {{ availablePrinters.find(p => p.id === selectedPrinter)?.name || '未选择' }}
            </span>
          </div>

          <div class="status-item">
            <label>打印机状态：</label>
            <span class="status-value" :class="{ 'status-ready': selectedPrinter, 'status-error': !selectedPrinter }">
              {{ selectedPrinter ? '就绪' : '未选择打印机' }}
            </span>
          </div>

          <div class="status-item">
            <label>可用打印机数量：</label>
            <span class="status-value">{{ availablePrinters.length }} 台</span>
          </div>

          <div class="status-item">
            <label>订单状态：</label>
            <span class="status-value" :class="{ 'status-ready': autoPrintEnabled, 'status-error': !autoPrintEnabled }">
              {{ autoPrintEnabled ? '自动监控中' : '已停止监控' }}
            </span>
          </div>

          <div class="status-item">
            <label>今日打印：</label>
            <span class="status-value">{{ todayPrintCount }} 单</span>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import toast from '../utils/toast.js'

export default {
  name: 'PrinterSystem',
  data() {
    return {
      profileInfo: null, // 存储profile接口返回的信息

      // 打印机相关
      selectedPrinter: '', // 当前选择的打印机ID
      availablePrinters: [], // 可用打印机列表（从系统获取）
      isLoadingPrinters: false, // 是否正在加载打印机列表

      // 订单打印相关
      autoPrintEnabled: false, // 是否开启自动打印
      isCheckingOrders: false, // 是否正在检查订单
      lastCheckTime: '', // 最后检查时间
      pendingOrdersCount: 0, // 待处理订单数量
      todayPrintCount: 0, // 今日打印数量
      autoPrintInterval: null, // 自动打印定时器
    }
  },
  methods: {
    // 打印机测试功能
    async testPrinter() {
      if (!this.selectedPrinter) {
        toast.warning('请先选择打印机')
        return
      }

      try {
        toast.info('开始打印机测试...')

        // 生成测试测试小票数据
        const testReceiptData = {
          header: {
            title: '润枫池 打印系统 - 测试',
            orderNo: 'TEST' + Date.now(),
            date: new Date().toLocaleString('zh-CN'),
            cashier: '系统管理员'
          },
          content: {
            message: '这是一张打印机测试小票',
            printerInfo: `当前打印机: ${this.availablePrinters.find(p => p.id === this.selectedPrinter)?.name || '未知打印机'}`,
            testItems: [
              '检查打印机连接状态',
              '验证打印内容格式',
              '确认打印质量清晰',
              '测试打印速度正常'
            ]
          },
          items: [
            { name: '测试商品1', quantity: 2, price: '10.00', subtotal: '20.00', isGift: false },
            { name: '测试商品2', quantity: 1, price: '15.00', subtotal: '15.00', isGift: false },
            { name: '赠品测试', quantity: 1, price: '0.00', subtotal: '0.00', isGift: true }
          ],
          summary: {
            totalAmount: '35.00',
            paidAmount: '35.00',
            changeAmount: '0.00',
            paymentMethod: '现金',
            memberInfo: '测试会员'
          }
        }

        // 调用打印API
        if (window.electronAPI && typeof window.electronAPI.printDocument === 'function') {
          const result = await window.electronAPI.printDocument({
            printerName: this.selectedPrinter,
            documentData: testReceiptData,
            documentName: `打印机测试_${Date.now()}`
          })

          if (result.success) {
            toast.success('打印机测试成功！')
          } else {
            throw new Error(result.error || '打印机测试失败')
          }
        } else {
          // Web环境模拟打印
          setTimeout(() => {
            toast.success('打印机测试成功（模拟模式）')
          }, 2000)
        }

      } catch (error) {
        console.error('打印机测试失败:', error)
        toast.error(`打印机测试失败：${error.message}`)
      }
    },

    // 处理打印机选择变化
    handlePrinterChange() {
      console.log('打印机选择已更改为:', this.selectedPrinter)
      // 保存选择到本地存储
      localStorage.setItem('selectedPrinter', this.selectedPrinter)

      const selectedPrinterName = this.availablePrinters.find(p => p.id === this.selectedPrinter)?.name || '未知打印机'
      toast.success(`已切换到 ${selectedPrinterName}`)
    },

    // 获取系统打印机列表
    async loadPrinters() {
      this.isLoadingPrinters = true
      try {
        // 检查是否在Electron环境中
        const electronAPI = window.electronAPI
        if (electronAPI && typeof electronAPI.getPrinters === 'function') {
          // 使用Electron API获取打印机列表
          try {
            const printers = await electronAPI.getPrinters()
            this.availablePrinters = printers.map(printer => ({
              id: printer.id || printer.name,
              name: printer.displayName || printer.name,
              isDefault: printer.isDefault || false
            }))
          } catch (apiError) {
            console.warn('Electron API调用失败，使用备用方案:', apiError)
            this.availablePrinters = await this.getWebPrinters()
          }
        } else {
          // Web环境或Electron API不可用，使用模拟数据
          this.availablePrinters = await this.getWebPrinters()
        }
        console.log('获取到打印机列表:', this.availablePrinters)

        // 从本地存储恢复上一次的选择
        const savedPrinter = localStorage.getItem('selectedPrinter')
        if (savedPrinter && this.availablePrinters.find(p => p.id === savedPrinter)) {
          this.selectedPrinter = savedPrinter
        } else {
          // 选择默认打印机
          const defaultPrinter = this.availablePrinters.find(p => p.isDefault)
          if (defaultPrinter) {
            this.selectedPrinter = defaultPrinter.id
          } else if (this.availablePrinters.length > 0) {
            // 如果没有默认打印机，选择第一个
            this.selectedPrinter = this.availablePrinters[0].id
          }
        }

      } catch (error) {
        console.error('获取打印机列表失败:', error)
        toast.error('获取打印机列表失败')
        // 设置默认打印机列表
        this.availablePrinters = [
          { id: 'default', name: '默认打印机', isDefault: true }
        ]
      } finally {
        this.isLoadingPrinters = false
      }
    },

    // Web环境下的打印机获取
    async getWebPrinters() {
      try {
        return new Promise((resolve) => {
          setTimeout(() => {
            // 默认模拟数据
            const printers = [
              { id: 'web_printer1', name: '系统默认打印机', isDefault: true },
              { id: 'web_printer2', name: 'PDF虚拟打印机', isDefault: false },
              { id: 'web_printer3', name: 'Microsoft Print to PDF', isDefault: false }
            ]
            resolve(printers)
          }, 300)
        })
      } catch (error) {
        console.error('获取Web打印机列表失败:', error)
        // 返回最基本的选项
        return [{ id: 'fallback', name: '默认打印机', isDefault: true }]
      }
    },

    // 刷新打印机列表
    async refreshPrinters() {
      console.log('刷新打印机列表...')
      await this.loadPrinters()
      toast.success('打印机列表已刷新')
    },

    
    // 切换自动打印状态
    toggleAutoPrint() {
      this.autoPrintEnabled = !this.autoPrintEnabled

      if (this.autoPrintEnabled) {
        this.startAutoPrint()
        toast.success('已开启自动打印监控')
      } else {
        this.stopAutoPrint()
        toast.info('已停止自动打印监控')
      }
    },

    // 开始自动打印
    startAutoPrint() {
      // 立即检查一次
      this.checkPendingOrders()

      // 设置定时器，每5秒检查一次
      this.autoPrintInterval = setInterval(() => {
        this.checkPendingOrders()
      }, 5000)
    },

    // 停止自动打印
    stopAutoPrint() {
      if (this.autoPrintInterval) {
        clearInterval(this.autoPrintInterval)
        this.autoPrintInterval = null
      }
    },

    // 检查待处理订单
    async checkPendingOrders() {
      if (this.isCheckingOrders || !this.selectedPrinter) {
        return
      }

      this.isCheckingOrders = true

      try {
        const response = await fetch('https://rfc.geslan.cn/index.php?s=/store/prt/get1')
        const result = await response.json()

        console.log('订单检查结果:', result)

        // 更新最后检查时间
        this.lastCheckTime = new Date().toLocaleString('zh-CN')

        if (result.status === 200 && result.data) {
          const orders = result.data.filter(order => order !== null)
          this.pendingOrdersCount = orders.length

          if (orders.length > 0) {
            toast.info(`发现 ${orders.length} 个待打印订单`)

            // 处理每个订单
            for (const order of orders) {
              try {
                await this.printOrder(order)
                await this.markOrderAsPrinted(order.order_id)
                this.todayPrintCount++
                toast.success(`订单 ${order.order_no} 打印成功`)
              } catch (error) {
                console.error(`打印订单 ${order.order_no} 失败:`, error)
                toast.error(`订单 ${order.order_no} 打印失败`)
              }
            }
          } else {
            console.log('没有待处理的订单')
          }
        } else {
          console.warn('获取订单数据失败:', result.message)
          this.pendingOrdersCount = 0
        }

      } catch (error) {
        console.error('检查订单时发生错误:', error)
        this.pendingOrdersCount = 0
        toast.error('检查订单失败，请检查网络连接')
      } finally {
        this.isCheckingOrders = false
      }
    },

    // 打印订单
    async printOrder(order) {
      if (!this.selectedPrinter) {
        throw new Error('请先选择打印机')
      }

      const orderData = {
        header: {
          title: '润枫池 打印系统',
          orderNo: order.order_no,
          date: order.create_time,
          cashier: order.user?.nick_name || '顾客'
        },
        items: order.goods.map(item => ({
          name: item.goods_name,
          quantity: item.total_num,
          price: (parseFloat(item.line_price) / parseFloat(item.total_num)).toFixed(2),
          subtotal: item.total_price,
          isGift: false
        })),
        summary: {
          totalAmount: order.total_price,
          paidAmount: order.pay_price,
          changeAmount: (parseFloat(order.pay_price) - parseFloat(order.total_price)).toFixed(2),
          paymentMethod: this.getPaymentMethodText(order.pay_method),
          memberInfo: order.user?.nick_name ? `会员: ${order.user.nick_name}` : '非会员'
        }
      }

      // 调用打印API
      if (window.electronAPI && typeof window.electronAPI.printDocument === 'function') {
        const result = await window.electronAPI.printDocument({
          printerName: this.selectedPrinter,
          documentData: orderData,
          documentName: `订单_${order.order_no}`
        })

        if (!result.success) {
          throw new Error(result.error || '打印失败')
        }
      } else {
        // Web环境模拟打印
        console.log('模拟打印订单:', orderData)
        await new Promise(resolve => setTimeout(resolve, 1000))
      }
    },

    // 标记订单为已打印
    async markOrderAsPrinted(orderId) {
      try {
        // 调用正确的API端点标记订单为已打印
        const response = await fetch(`https://rfc.geslan.cn/index.php?s=/store/prt/setok&orderId=${orderId}`)
        const result = await response.json()

        console.log(`订单 ${orderId} 标记为已打印:`, result)

        if (result.status !== 200 && result.status !== 'success') {
          console.warn(`标记订单 ${orderId} 为已打印失败:`, result.message || '未知错误')
        }
      } catch (error) {
        console.error('标记订单已打印失败:', error)
      }
    },

    // 获取支付方式文本
    getPaymentMethodText(payMethod) {
      const methods = {
        'wechat': '微信支付',
        'alipay': '支付宝',
        'cash': '现金',
        'card': '银行卡'
      }
      return methods[payMethod] || payMethod
    }
  },
  mounted() {
    // 设置页面标题
    document.title = '润枫池 打印系统'
    this.loadPrinters() // 加载打印机列表
  }
}
</script>

<style scoped>
.printer-layout {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  font-family: 'Microsoft YaHei', -apple-system, BlinkMacSystemFont, sans-serif;
  display: flex;
  flex-direction: column;
}

/* 头部样式 */
.header {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
  padding: 20px 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-title h1 {
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  background: linear-gradient(135deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.header-icon {
  width: 40px;
  height: 40px;
  object-fit: contain;
  filter: brightness(0) saturate(100%) invert(46%) sepia(79%) saturate(374%) hue-rotate(182deg) brightness(90%) contrast(85%);
}

/* 主内容区域 */
.main-content {
  flex: 1;
  padding: 40px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: start;
}

/* 面板通用样式 */
.printer-panel, .status-panel {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-radius: 24px;
  padding: 32px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.panel-header {
  margin-bottom: 32px;
  padding-bottom: 20px;
  border-bottom: 2px solid rgba(102, 126, 234, 0.1);
}

.panel-header h2 {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* 打印机选择器 */
.printer-selector {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;
}

.selector-label {
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  min-width: 100px;
}

.printer-select {
  flex: 1;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 14px;
  background: white;
  color: #374151;
  outline: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.printer-select:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.refresh-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #52c41a, #389e0d);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(82, 196, 26, 0.3);
  white-space: nowrap;
}

.refresh-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(82, 196, 26, 0.4);
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.refresh-btn .refresh-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
  filter: brightness(0) invert(1);
  transition: all 0.3s ease;
}

.refresh-btn .refresh-icon.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 测试区域 */
.test-section {
  text-align: center;
}

.test-section h3 {
  margin: 0 0 12px 0;
  font-size: 18px;
  font-weight: 600;
  color: #374151;
}

.test-description {
  margin: 0 0 24px 0;
  color: #64748b;
  font-size: 14px;
  line-height: 1.5;
}

.test-print-btn {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 16px 32px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  border-radius: 16px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.3);
  position: relative;
  overflow: hidden;
}

.test-print-btn::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
}

.test-print-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 12px 35px rgba(102, 126, 234, 0.4);
}

.test-print-btn:active::before {
  width: 400px;
  height: 400px;
}

.test-print-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.test-print-btn .btn-icon {
  width: 24px;
  height: 24px;
  object-fit: contain;
  filter: brightness(0) invert(1);
}

/* 状态信息 */
.status-info {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.status-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: rgba(248, 250, 252, 0.8);
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.status-item:hover {
  background: rgba(102, 126, 234, 0.05);
  border-color: rgba(102, 126, 234, 0.2);
  transform: translateX(4px);
}

.status-item label {
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
}

.status-value {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.status-value.status-ready {
  color: #52c41a;
  font-weight: 700;
}

.status-value.status-error {
  color: #ef4444;
  font-weight: 700;
}

/* 订单控制区域样式 */
.order-controls {
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: rgba(248, 250, 252, 0.8);
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-radius: 12px;
}

.auto-print-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  background: white;
  color: #64748b;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.auto-print-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-color: #667eea;
}

.auto-print-btn.active {
  background: linear-gradient(135deg, #52c41a, #22c55e);
  color: white;
  border-color: #52c41a;
  box-shadow: 0 4px 15px rgba(82, 196, 26, 0.3);
}

.auto-print-btn.active:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(82, 196, 26, 0.4);
}

.checking-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(102, 126, 234, 0.1);
  border-radius: 8px;
  color: #667eea;
  font-size: 13px;
  font-weight: 600;
}

.checking-indicator.disabled {
  background: rgba(148, 163, 184, 0.1);
  color: #94a3b8;
}

.order-check-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: 2px solid #f59e0b;
  border-radius: 12px;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(245, 158, 11, 0.3);
}

.order-check-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(245, 158, 11, 0.4);
}

.order-check-btn:disabled {
  background: linear-gradient(135deg, #94a3b8, #64748b);
  border-color: #94a3b8;
  cursor: not-allowed;
  opacity: 0.7;
}

.print-status {
  margin-top: 16px;
  padding: 12px 16px;
  background: rgba(248, 250, 252, 0.6);
  border-radius: 8px;
  border: 1px solid rgba(148, 163, 184, 0.2);
}

.print-status p {
  margin: 0 0 8px 0;
  font-size: 13px;
  color: #64748b;
}

.print-status p:last-child {
  margin-bottom: 0;
}

.pending-count {
  color: #f59e0b !important;
  font-weight: 600;
}

.pending-count span {
  color: #dc2626;
  font-weight: 700;
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .main-content {
    grid-template-columns: 1fr;
    padding: 20px;
    gap: 20px;
  }

  .header {
    padding: 16px 20px;
  }

  .header-title h1 {
    font-size: 24px;
  }

  .printer-panel, .status-panel {
    padding: 24px;
  }

  .printer-selector {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .selector-label {
    min-width: auto;
  }
}

@media (max-width: 640px) {
  .header {
    padding: 12px 16px;
  }

  .header-title {
    gap: 12px;
  }

  .header-title h1 {
    font-size: 20px;
  }

  .header-icon {
    width: 32px;
    height: 32px;
  }

  .header-user {
    gap: 12px;
  }

  .main-content {
    padding: 16px;
  }

  .printer-panel, .status-panel {
    padding: 20px;
  }

  .panel-header h2 {
    font-size: 20px;
  }

  .test-print-btn {
    padding: 14px 24px;
    font-size: 14px;
  }
}
</style>